import { Stack } from "@mui/material";

import ModalDialog, { type DialogActionButtons } from "~/components/ModalDialog";
import { BodyText, BodyTextSmall } from "~/components/Typographies";
import firstRunLinks from "~/helpers/firstRunLinks";
import { contactLinkAnchorProps } from "~/helpers/resolveContactLink";
import trialLimitLabel from "~/helpers/trialLimitLabel";
import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import { clearTrialLimit } from "~/store/trialLimitSlice";

const headline = (label: string | null): string =>
	label
		? `You've reached your trial limit for ${label}. Talk to us to keep going.`
		: "You've reached a trial limit. Talk to us to keep going.";

const usageDetail = (label: string | null, cap: number, current: number | null): string => {
	const allowance = `Your trial includes ${cap} ${label ?? "of these"}`;
	const usage = current === null ? "" : ` and you're using ${current}`;
	return `${allowance}${usage}. Everything you've already created stays exactly where it is — you can keep working.`;
};

const TrialLimitDialog = () => {
	const dispatch = useAppDispatch();
	const { cap, current, limit, reached } = useAppSelector((state) => state.trialLimit);

	const close = () => dispatch(clearTrialLimit());
	const label = trialLimitLabel(limit);
	const contactUrl = firstRunLinks.trialContactUrl;

	const contactAction: DialogActionButtons[] = contactUrl
		? [
				{
					children: "Talk to us",
					props: {
						color: "primary",
						href: contactUrl,
						onClick: close,
						variant: "contained",
						...contactLinkAnchorProps(contactUrl)
					}
				}
			]
		: [];

	const actions: DialogActionButtons[] = [
		...contactAction,
		{ children: "Dismiss", props: { color: "inherit", onClick: close, variant: "text" } }
	];

	return (
		<ModalDialog actions={actions} close={close} open={reached} size="small" title="You've reached a trial limit">
			<Stack spacing={1}>
				<BodyText>{headline(label)}</BodyText>
				{cap !== null && <BodyTextSmall color="text.secondary">{usageDetail(label, cap, current)}</BodyTextSmall>}
			</Stack>
		</ModalDialog>
	);
};

export default TrialLimitDialog;
